import React from "react";
import { motion } from "framer-motion";
import {
  CheckCircle,
  BookOpen,
  Brain,
  Target,
  Award,
} from "lucide-react";
import ProgramPageLayout from "../../components/shared/ProgramPageLayout";

const AcademicExcellence: React.FC = () => {
  return (
    <ProgramPageLayout slug="academic-consulting" theme="blue">
      {(program) => (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Program Features
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {program.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {feature}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Expert {feature.toLowerCase()} with personalized
                    academic guidance.
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Academic Consulting Benefits */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Why Choose Our Academic Consulting?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <Brain className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Learning Style Assessment
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Comprehensive evaluation of your unique learning
                    preferences and capabilities.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Target className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Strategic Planning
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Customized academic roadmap aligned with your goals and
                    aspirations.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <BookOpen className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Study Optimization
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Evidence-based techniques to maximize learning
                    efficiency and retention.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Award className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Expert Guidance
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Access to experienced academic consultants and education
                    specialists.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Process Overview */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Our Consulting Process
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Initial Assessment
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Comprehensive evaluation of academic strengths,
                    challenges, and goals.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Strategic Planning
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Development of personalized academic strategy and action
                    plan.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Implementation Support
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Ongoing guidance and support throughout the
                    implementation process.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">
                  4
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Progress Monitoring
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Regular check-ins and adjustments to ensure optimal
                    outcomes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </ProgramPageLayout>
  );
};

export default AcademicExcellence;
